import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api';
import { seasonPassEndpoints } from '../api-comps';
import type { IssuePlan } from '../api-comps';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen, statusTone } from '../ui';
import { INPUT } from './event/shared';

/**
 * Season passes (D-373): one QR for every regular-season home game.
 *
 * Issued to households (host families first): a row of the list is a name, an
 * email and how many passes, and each pass admits one person per game. The
 * household's passes are emailed together with a PDF of their QR codes and are
 * on their account. At the gate a scan becomes a general-admission comp ticket
 * for that night; playoff games (ticked on the game's Overview tab) and other
 * ballpark events are not covered.
 */
export default function SeasonPassesPage() {
  const can = usePermissions();
  const [error, setError] = useState<string | null>(null);
  const [seasonId, setSeasonId] = useState('');
  const [q, setQ] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const options = useQuery({ queryKey: ['season-passes', 'options'], queryFn: () => seasonPassEndpoints.options(), enabled: can('season_passes.manage') });
  const season = seasonId || options.data?.seasons[0]?.id || '';
  const passes = useQuery({ queryKey: ['season-passes', season, q], queryFn: () => seasonPassEndpoints.list(season, q), enabled: Boolean(season) && can('season_passes.manage') });
  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['season-passes'] });
  };
  const onError = (cause: ApiError) => setError(cause.message);

  if (!can('season_passes.manage')) return <NotPermitted permission="season_passes.manage" />;
  const rows = passes.data?.passes ?? [];

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel
        title="Season passes"
        description="Each pass admits one person to every regular-season home game: scanned at the gate, it becomes a general-admission comp ticket for that night. Holders can also reserve seats for a game from their account or at the window."
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <select aria-label="Season" value={season} onChange={(e) => setSeasonId(e.target.value)} className={INPUT}>
              {(options.data?.seasons ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <input aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email" className={`w-48 ${INPUT}`} />
            {!issuing ? (
              <Button variant="primary" onClick={() => setIssuing(true)} disabled={!season}>
                Issue passes
              </Button>
            ) : null}
          </div>
        }
      >
        {issuing ? (
          <IssueForm
            seasonId={season}
            reasons={options.data?.comp_reasons ?? []}
            onDone={() => {
              setIssuing(false);
              refresh();
            }}
            onCancel={() => setIssuing(false)}
            onError={onError}
          />
        ) : null}
        <Table headers={['Pass', 'Holder', 'Status', 'Games', 'Valid', '']}>
          {rows.length === 0 ? <EmptyRow columns={6}>{passes.isLoading ? 'Loading…' : 'No passes for this season yet.'}</EmptyRow> : null}
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-line">
              <td className="px-5 py-2.5">
                <span className="font-medium text-ink-900">{row.label}</span>
                <span className="ml-2 font-mono text-xs text-ink-400">{row.number}</span>
              </td>
              <td className="px-5 py-2.5 text-ink-600">
                {row.holder_name}
                <br />
                <span className="text-xs">{row.holder_email}</span>
              </td>
              <td className="px-5 py-2.5">
                <Badge tone={statusTone(row.status)}>{row.status.toLowerCase()}</Badge>
              </td>
              <td className="tabular px-5 py-2.5">{row.games_attended ?? 0}</td>
              <td className="px-5 py-2.5 text-xs">
                {row.valid_from} to {row.valid_to}
              </td>
              <td className="px-5 py-2.5 text-right">
                <Button onClick={() => setOpen(open === row.id ? null : row.id)}>{open === row.id ? 'Close' : 'Open'}</Button>
              </td>
            </tr>
          ))}
        </Table>
      </Panel>
      {open ? <PassDetail id={open} onChanged={refresh} onError={onError} /> : null}
    </div>
  );
}

function IssueForm({ seasonId, reasons, onDone, onCancel, onError }: { seasonId: string; reasons: { id: string; code: string; name: string }[]; onDone: () => void; onCancel: () => void; onError: (error: ApiError) => void }) {
  const [text, setText] = useState('');
  const [ticketType, setTicketType] = useState('ADULT');
  const [reason, setReason] = useState(reasons.find((r) => r.code === 'HOST_FAMILY')?.id ?? '');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [plan, setPlan] = useState<IssuePlan | null>(null);
  const body = (dryRun: boolean) => ({ season_id: seasonId, ticket_type_code: ticketType.trim().toUpperCase(), comp_reason_id: reason || null, valid_from: validFrom || null, valid_to: validTo || null, text, send_email: sendEmail, dry_run: dryRun });
  const preview = useMutation({ mutationFn: () => seasonPassEndpoints.issue(body(true)), onSuccess: setPlan, onError });
  const issue = useMutation({ mutationFn: () => seasonPassEndpoints.issue(body(false)), onSuccess: onDone, onError });
  return (
    <div className="space-y-4 border-b border-line px-5 py-4">
      <Field label="Holders" htmlFor="sp-text" hint="One household per line: name, email, number of passes — and optionally each person's name, separated by semicolons. Paste from a spreadsheet; a header row is skipped.">
        <textarea
          id="sp-text"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setPlan(null);
          }}
          rows={6}
          placeholder={'Smith family, smith@example.com, 4\nPat Jones, pat@example.com, 2, Pat Jones; Sam Jones'}
          className={`w-full font-mono ${INPUT}`}
        />
      </Field>
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Ticket type code" htmlFor="sp-type" hint="Issued at the gate and when reserving">
          <input id="sp-type" value={ticketType} onChange={(e) => setTicketType(e.target.value)} className={`w-32 ${INPUT}`} />
        </Field>
        <Field label="Comp reason" htmlFor="sp-reason">
          <select id="sp-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={INPUT}>
            {reasons.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Valid from" htmlFor="sp-from" hint="Empty: the season's first day">
          <input id="sp-from" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className={INPUT} />
        </Field>
        <Field label="Valid to" htmlFor="sp-to" hint="Empty: the season's last day">
          <input id="sp-to" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} className={INPUT} />
        </Field>
        <label className="flex items-center gap-2 pb-1.5 text-sm text-ink-900">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
          email each household its passes
        </label>
      </div>
      {plan ? (
        <div className="rounded border border-line p-3 text-sm">
          <p className="font-medium">
            {plan.total_passes} pass(es) for {plan.holders.length} household(s), valid {plan.valid_from} to {plan.valid_to}
          </p>
          <ul className="mt-2 space-y-1 text-ink-600">
            {plan.holders.map((h) => (
              <li key={h.email}>
                {h.name} &lt;{h.email}&gt;: {h.labels.join(' · ')}
              </li>
            ))}
          </ul>
          {plan.problems.length > 0 ? (
            <ul className="mt-2 space-y-1 text-bad">
              {plan.problems.map((p) => (
                <li key={p.line}>
                  Line {p.line}: {p.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <div className="flex gap-2">
        <Button onClick={() => preview.mutate()} disabled={!text.trim() || preview.isPending}>
          Preview
        </Button>
        <Button variant="primary" onClick={() => issue.mutate()} disabled={!plan || plan.problems.length > 0 || issue.isPending}>
          {issue.isPending ? 'Issuing…' : 'Issue passes'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function PassDetail({ id, onChanged, onError }: { id: string; onChanged: () => void; onError: (error: ApiError) => void }) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const detail = useQuery({ queryKey: ['season-passes', 'detail', id], queryFn: () => seasonPassEndpoints.detail(id) });
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['season-passes', 'detail', id] });
    onChanged();
  };
  const change = useMutation({ mutationFn: ({ action, reason }: { action: 'suspend' | 'reactivate' | 'revoke' | 'reissue'; reason: string | null }) => seasonPassEndpoints.change(id, action, { reason, send_email: true }), onSuccess: done, onError });
  const email = useMutation({ mutationFn: () => seasonPassEndpoints.emailHolder(detail.data!.pass.holder_sub, detail.data!.pass.season_id), onSuccess: (r) => setNotice(`Sent ${r.passes} pass(es) to ${r.to_email}`), onError });
  const google = useMutation({ mutationFn: () => seasonPassEndpoints.googleWallet(id), onSuccess: (r) => window.open(r.url, '_blank', 'noopener'), onError });
  if (!detail.data) return <Panel title="Pass">{detail.isLoading ? <p className="px-5 py-4 text-sm text-ink-500">Loading…</p> : null}</Panel>;
  const { pass, games, history, household } = detail.data;
  const ask = (action: 'suspend' | 'revoke' | 'reissue', question: string) => {
    const reason = window.prompt(question);
    if (reason) change.mutate({ action, reason });
  };
  return (
    <Panel
      title={pass.label}
      description={`${pass.number} · ${pass.holder_name ?? ''} <${pass.holder_email ?? 'no email'}> · ${pass.status.toLowerCase()}${pass.status_reason ? ` (${pass.status_reason})` : ''} · QR version ${pass.credential_version}`}
      actions={
        <div className="flex flex-wrap gap-2">
          <a className="rounded border border-line px-3 py-1.5 text-sm" href={seasonPassEndpoints.pdfUrl(id)} target="_blank" rel="noreferrer">
            Print
          </a>
          <a className="rounded border border-line px-3 py-1.5 text-sm" href={seasonPassEndpoints.holderPdfUrl(pass.holder_sub, pass.season_id)} target="_blank" rel="noreferrer">
            Print household
          </a>
          <Button onClick={() => email.mutate()} disabled={email.isPending}>
            Email household
          </Button>
          <Button onClick={() => google.mutate()} disabled={google.isPending || pass.status !== 'ACTIVE'}>
            Google Wallet
          </Button>
          <a className="rounded border border-line px-3 py-1.5 text-sm" href={seasonPassEndpoints.appleUrl(id)}>
            Apple Wallet
          </a>
          {pass.status === 'ACTIVE' ? <Button onClick={() => ask('suspend', 'Why suspend this pass? Its QR stops scanning until reactivated.')}>Suspend</Button> : null}
          {pass.status === 'SUSPENDED' ? <Button onClick={() => change.mutate({ action: 'reactivate', reason: null })}>Reactivate</Button> : null}
          {pass.status !== 'REVOKED' ? <Button onClick={() => ask('reissue', 'Why reissue? The old QR stops scanning and the new one is emailed.')}>Reissue</Button> : null}
          {pass.status !== 'REVOKED' ? (
            <Button variant="danger" onClick={() => ask('revoke', 'Why revoke this pass for good?')}>
              Revoke
            </Button>
          ) : null}
        </div>
      }
    >
      {notice ? <p className="border-b border-line px-5 py-2 text-sm text-ok">{notice}</p> : null}
      {household.length > 0 ? <p className="border-b border-line px-5 py-2 text-xs text-ink-500">Same household: {household.map((h) => `${h.label} (${h.status.toLowerCase()})`).join(' · ')}</p> : null}
      <Table headers={['Game', 'Ticket', 'How', 'Scanned in']}>
        {games.length === 0 ? <EmptyRow columns={4}>Not used yet.</EmptyRow> : null}
        {games.map((g) => (
          <tr key={g.ticket_id} className="border-b border-line">
            <td className="px-5 py-2">
              {g.title} · {formatWhen(g.starts_at)}
            </td>
            <td className="px-5 py-2">
              {g.seat_label ?? '—'} · {g.order_number} {g.item_status !== 'ACTIVE' ? <Badge tone="warn">{g.item_status.toLowerCase()}</Badge> : null}
            </td>
            <td className="px-5 py-2">{g.channel === 'COMP' ? 'At the gate' : g.channel === 'WEB' ? 'Reserved online' : 'At the window'}</td>
            <td className="px-5 py-2">{g.admitted_at ? formatWhen(g.admitted_at) : '—'}</td>
          </tr>
        ))}
      </Table>
      <ul className="space-y-1 border-t border-line px-5 py-3 text-xs text-ink-500">
        {history.map((h, i) => (
          <li key={i}>
            {formatWhen(h.occurred_at)} · {h.event_type.toLowerCase()} (v{h.credential_version}){h.staff ? ` by ${h.staff}` : ''}
            {h.reason ? ` — ${h.reason}` : ''}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
